import React from 'react';
import I18n from 'i18n-js';
import { isEmpty } from 'lodash';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import {
  core_apimessages_ProtectionPlan,
  Item2PricingDataFragment,
} from '@reverbdotcom/commons/src/gql/graphql';
import {
  RCFlex,
  RCAlertBox,
  RCSkeleton,
} from '@reverbdotcom/cadence/components';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { trackProtectionPlanEvent } from '@reverbdotcom/commons/src/components/protectionPlanHelpers';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import ProtectionPlanModal from '@reverbdotcom/commons/src/components/ProtectionPlanModal';
import { useEppoAssignment } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';

interface Props {
  availableProtectionPlans: core_apimessages_ProtectionPlan[];
  listing: Item2PricingDataFragment;
  setProtectionPlanId: (protectionPlanId) => void;
  protectionPlanId: string;
  disabled: boolean;
  loading: boolean;
}

interface CheckBoxLabelProps {
  protectionPlan: core_apimessages_ProtectionPlan;
  checked: boolean;
  disabled: boolean;
  treatment: 0 | 1 | 2;
}

const COMPONENT_NAME = 'ItemPageProtectionPlanUpperAddOn';

function ProtectionPlanCheckboxLabel({
  protectionPlan,
  checked,
  disabled,
  treatment,
}: CheckBoxLabelProps) {
  const durationYears = (Number(protectionPlan.durationMonths) / 12);
  const planOptionText = getPlanOptionText(durationYears, treatment);

  return (
    <label
      className="item-page-protection-plan-upper__checkbox-label"
      data-checked={checked || null}
      data-disabled={disabled || null}
    >
      <SanitizedRender
        html={I18n.t(
          planOptionText,
          {
            amount: protectionPlan.amount.display,
            durationYears,
            boldTagOpen: '<span class="weight-bold">',
            normalTagOpen: `<span class="weight-normal ${treatment === 1 && 'color-secondary'}">`,
            spanTagClose: '</span>',
          },
        )}
      />
    </label>
  );
}

function getPlanOptionText(durationYears: number, treatment: 0 | 1 | 2) {
  const isAlt = treatment === 2;
  const suffix = durationYears === 1 ? 'OneYear' : '';
  const prefix = isAlt ? 'altPlanOption' : 'planOption';

  return `discovery.protectionPlans.itemPage.upperAddOn.${prefix}${suffix}`;
}

function getMonthlyCost(plan: core_apimessages_ProtectionPlan) {
  return (Number(plan.amount.amount) / Number(plan.durationMonths)).toFixed(2);
}

function modalTrigger() {
  return (
    <button
      type="button"
      className="item-page-protection-plan-upper__change-plan"
    >
      {I18n.t('discovery.protectionPlans.itemPage.changePlan')}
    </button>
  );
}

function Skeleton() {
  return (
    <div className="item-page-protection-plan-upper--loading">
      <RCSkeleton height="10rem" display="block" />
    </div>
  );
}

export function ItemPageProtectionPlanUpperAddOn({
  availableProtectionPlans,
  listing,
  setProtectionPlanId,
  protectionPlanId,
  disabled,
  loading,
}: Props) {
  const [isProtectionPlanModalOpen, setProtectionPlanModalOpen] = React.useState(false);
  const lastSelectedProtectionPlanId = React.useRef(null);
  const [ defaultFetchedProtectionPlan ] = availableProtectionPlans;
  const selectedProtectionPlan = availableProtectionPlans.find(pp => pp.planId === protectionPlanId);
  const displayedProtectionPlan = selectedProtectionPlan || defaultFetchedProtectionPlan;

  // Mulberry component iteration experiment
  const iterationExpAssignment = useEppoAssignment({ flagKey: eppoFlags.ITEM_PAGE_MULBERRY_COMPONENT_ITERATION });
  const isTreatment1 = iterationExpAssignment === 'treatment_1';
  const isTreatment2 = iterationExpAssignment === 'treatment_2';

  // Store the last protection plan that was manually selected by the user
  React.useEffect(() => {
    if (!disabled) lastSelectedProtectionPlanId.current = protectionPlanId;
  }, [protectionPlanId]);

  // Remove the selected protection plan if disabled, otherwise restore it
  React.useEffect(() => {
    if (disabled) setProtectionPlanId(null);
    else setProtectionPlanId(lastSelectedProtectionPlanId.current);
  }, [disabled]);

  function onSelectProtectionPlan(plan: core_apimessages_ProtectionPlan) {
    if (disabled) return;

    const eventName = plan.planId === selectedProtectionPlan?.planId ? MParticleEventName.ClickedRemovedProtectionPlan : MParticleEventName.ClickedAddedProtectionPlan;

    if (plan.planId === selectedProtectionPlan?.planId) {
      setProtectionPlanId(null);
    } else {
      setProtectionPlanId(plan.planId);
    }

    trackProtectionPlanEvent(
      COMPONENT_NAME,
      eventName,
      plan,
      listing,
    );
  }

  function onUpdate(selectedPlan) {
    setProtectionPlanId(selectedPlan.planId);
  }

  if ((isEmpty(availableProtectionPlans) && !loading)) return null;

  return (
    <>
      {loading ? (
        <Skeleton />
      ) : (
        <div className="item-page-protection-plan-upper">
          <div>
            <span
              style={isTreatment1 ? { display: 'block', fontSize: 'var(--rc-font-size-400)' } : {}}
              className="item-page-protection-plan-upper__body"
            >
              <Translate
                html
                text={`discovery.protectionPlans.itemPage.upperAddOn.${isTreatment1 ? 'altBody' : 'body' }`}
              />
            </span>
            {isTreatment1 && (
              <span className="item-page-protection-plan-upper__description">
                <Translate
                  html
                  text="discovery.protectionPlans.itemPage.upperAddOn.description"
                />
              </span>
            )}
            <span>
              <ProtectionPlanModal
                onToggle={setProtectionPlanModalOpen}
                isOpen={isProtectionPlanModalOpen}
                availableProtectionPlans={availableProtectionPlans}
                listingId={listing.id}
                disabled={disabled}
                protectionPlan={displayedProtectionPlan}
                trackEventFn={(componentName, protectionPlan) => trackProtectionPlanEvent(componentName, MParticleEventName.ClickedAddedProtectionPlan, protectionPlan, listing)}
                modalTrigger={modalTrigger}
                onUpdate={onUpdate}
              />
            </span>
          </div>
          <div className="width-100">
            <RCFlex gap="1" justify="space-between">
              {availableProtectionPlans.map((plan) => {
                const isChecked = selectedProtectionPlan?.planId === plan.planId;

                return (
                  <button
                    key={plan.planId}
                    aria-pressed={isChecked}
                    disabled={disabled}
                    className="item-page-protection-plan-upper__checkbox-button"
                    style={isTreatment2 ? { paddingBottom: 'var(--rc-space-2)' } : {}}
                    onClick={() => onSelectProtectionPlan(plan)}
                  >
                    <ProtectionPlanCheckboxLabel
                      protectionPlan={plan}
                      checked={isChecked}
                      disabled={disabled}
                      treatment={isTreatment1 ? 1 : isTreatment2 ? 2 : 0}
                    />
                    {isTreatment2 && (
                      <span className="item-page-protection-plan-upper__checkbox-monthly-cost">
                        {I18n.t('discovery.protectionPlans.itemPage.upperAddOn.monthlyCost', {
                          amount: getMonthlyCost(plan),
                        })}
                      </span>
                    )}
                  </button>
                );
              })}
            </RCFlex>
          </div>
          {disabled && (
            <div className="item-page-protection-plan-upper__alert">
              <RCAlertBox type="warning" small>
                <Translate text="discovery.protectionPlans.itemPage.upperAddOn.bulkItemsAlert" />
              </RCAlertBox>
            </div>
          )}
        </div>
      )}
    </>
  );
}
